import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  PageContainer,
  ContentRow,
  Card,
  Button,
  SearchInput,
  Table,
  TableHeader,
  TableRow,
  TableCell,
  StatusBadge,
  TabContainer,
  TabList,
  Tab,
  TabContent,
  fadeIn,
  formatDate
} from '../styles/PageStyles';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons';
import PageHeaderWithInfo from '../components/common/PageHeaderWithInfo';
import { getEmailTemplates, getEmailTemplateCount, updateEmailTemplate } from '../services/emailService';
import { useNavigate } from 'react-router-dom';
import Pagination from '../components/common/Pagination';
import { usePermission } from '../context/PermissionContext';

const EmailTemplates = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debounceSearchQuery, setDebounceSearchQuery] = useState(searchTerm);
  const [activeTab, setActiveTab] = useState('ACTIVE');
  const [templateList, setTemplateList] = useState([]);

  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const { permissions } = usePermission();
  
  const userAccessPermissions = permissions?.length
      ? permissions
        .filter(ele => ele.component_type === "prompt") // Reuse prompt permissions or fall back to basic admin permissions
        .map(ele => ele.permission.toLowerCase())
      : ['create', 'read', 'update', 'delete']; // Default permissions if not strictly defined
  
  const itemsPerPage = 10;

  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => { setDebounceSearchQuery(searchTerm) }, 300);
    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  const handleToggleActivation = async (templateId, currentStatus) => {
    try {
      const templateToUpdate = templateList.find(t => t.id === templateId);
      if (!templateToUpdate) return;

      const newStatus = currentStatus === 'ACTIVE' ? 'CLOSED' : 'ACTIVE';
      
      // If we are activating a template, archive all other active/draft templates with the same code first
      if (newStatus === 'ACTIVE') {
        const filter = {
          code: templateToUpdate.code,
          status: ['ACTIVE', 'DRAFT']
        };
        const activeTemplates = await getEmailTemplates(filter, {}, { skip: 0, limit: 100 });
        
        for (const t of activeTemplates) {
          if (t.id !== templateId) {
            await updateEmailTemplate({
              ...t,
              status: 'CLOSED'
            });
          }
        }
      }

      await updateEmailTemplate({
        ...templateToUpdate,
        status: newStatus
      });
      
      // Refresh the list
      await fetchTemplatesData();
    } catch (error) {
      console.error('Error toggling template status:', error);
    }
  };

  const fetchTemplatesData = useCallback(async () => {
    let filter = {};

    if (debounceSearchQuery) {
      filter = { 'name': { 'ilike': `${debounceSearchQuery}` } };
    }

    if (activeTab === 'ACTIVE') {
      filter = { ...filter, 'status': ['ACTIVE', 'DRAFT'] };
    } else {
      filter = { ...filter, 'status': 'CLOSED' };
    }

    const sort = { updated_at: 'DESC' };
    const query = { skip: (currentPage - 1) * itemsPerPage, limit: itemsPerPage };

    try {
      const response = await getEmailTemplates(filter, sort, query);
      setTemplateList(response || []);
    } catch (error) {
      console.error('Error fetching email templates:', error);
    }
  }, [currentPage, activeTab, debounceSearchQuery]);

  useEffect(() => {
    fetchTemplatesData();
  }, [fetchTemplatesData]);

  useEffect(() => {
    const fetchCount = async () => {
      try {
        let filter = {};
        if (debounceSearchQuery) {
          filter = { 'name': { 'ilike': `${debounceSearchQuery}` } };
        }
        if (activeTab === 'ACTIVE') {
          filter = { ...filter, 'status': ['ACTIVE', 'DRAFT'] };
        } else {
          filter = { ...filter, 'status': 'CLOSED' };
        }
        const count = await getEmailTemplateCount(filter);
        setTotalItems(count || 0);
      } catch (error) {
        console.log("Error fetching email template count:", error);
      }
    };

    fetchCount();
    setCurrentPage(1);
  }, [activeTab, debounceSearchQuery]);

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'active';
      case 'draft': return 'default';
      case 'closed': return 'warning';
      default: return 'default';
    }
  };

  // Handle template selection
  const handleTemplateSelect = (templateId) => {
    navigate(`/email-template-details/${templateId}`);
  };

  const handleTemplateCopy = (template) => {
    navigate(`/email-template-details/new`, {
      state: { ...template }
    });
  };

  return (
    <PageContainer>
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
      >
        <PageHeaderWithInfo
          icon={faEnvelope}
          title="Templates"
          description="Manage HTML and plain text email and document templates, set dynamic placeholders, and define message content for pipeline notifications."
        >
          <div style={{ display: 'flex', gap: '1rem' }}>
            <SearchInput
              type="text"
              placeholder="Search templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {userAccessPermissions.includes('create') && (
              <Button primary onClick={() => handleTemplateSelect('new')}>
                Create Template
              </Button>
            )}
          </div>
        </PageHeaderWithInfo>

        <TabContainer>
          <TabList>
            <Tab active={activeTab === 'ACTIVE'} onClick={() => setActiveTab('ACTIVE')}>Active Templates</Tab>
            <Tab active={activeTab === 'CLOSED'} onClick={() => setActiveTab('CLOSED')}>Archived Templates</Tab>
          </TabList>

          <TabContent active={activeTab === 'ACTIVE' || activeTab === 'CLOSED'}>
            <ContentRow>
              <div style={{ flex: '1 1 60%' }}>
                <Card>
                  <Table>
                    <thead>
                      <tr>
                        <TableHeader>Name</TableHeader>
                        <TableHeader>Code</TableHeader>
                        <TableHeader>Type</TableHeader>
                        <TableHeader>Category</TableHeader>
                        <TableHeader>Version</TableHeader>
                        <TableHeader>Last Modified</TableHeader>
                        <TableHeader>Status</TableHeader>
                        <TableHeader>Actions</TableHeader>
                      </tr>
                    </thead>
                    <tbody>
                      {templateList.map(template => (
                        <TableRow
                          key={template?.id}
                        >
                          <TableCell>{template?.name}</TableCell>
                          <TableCell>{template?.code}</TableCell>
                          <TableCell>{template?.type === 'attachment' ? 'Attachment' : 'Email Body'}</TableCell>
                          <TableCell>{template?.category}</TableCell>
                          <TableCell>{template?.version || '1'}</TableCell>
                          <TableCell>{formatDate(template?.updated_at)}</TableCell>
                          <TableCell>
                            <StatusBadge status={getStatusColor(template?.status)}>
                              {template?.status?.charAt(0).toUpperCase() + template?.status?.slice(1).toLowerCase()}
                            </StatusBadge>
                          </TableCell>
                          <TableCell>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              {template?.status !== 'CLOSED' && (
                                <Button 
                                  style={{ width: '40px', padding: '0.3rem', fontSize: '0.8rem' }}
                                  onClick={() => handleTemplateSelect(template?.id)}
                                >
                                  {userAccessPermissions.includes('update') ? 'Edit' : 'View'}
                                </Button>
                              )}

                              {userAccessPermissions.includes('update') && (
                                <Button
                                  primary={template?.status === 'DRAFT' || template?.status === 'CLOSED'}
                                  style={{ width: '78px', padding: '0.3rem', fontSize: '0.8rem' }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleActivation(template?.id, template?.status);
                                  }}
                                >
                                  {template?.status === 'ACTIVE' ? 'Archive' : 'Activate'}
                                </Button>
                              )}

                              {userAccessPermissions.includes('create') && (
                                <Button 
                                  style={{ width: '50px', padding: '0.3rem', fontSize: '0.8rem' }}
                                  onClick={() => handleTemplateCopy(template)}
                                >
                                  Copy
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                      {templateList.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                            No email templates found.
                          </TableCell>
                        </TableRow>
                      )}
                    </tbody>
                  </Table>
                </Card>
            
                {totalItems > itemsPerPage && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalCount={totalItems}
                    setCurrentPage={setCurrentPage}
                    setTotalPages={setTotalPages}
                  />
                )}
              </div>
            </ContentRow>
          </TabContent>
        </TabContainer>
      </motion.div>
    </PageContainer>
  );
};

export default EmailTemplates;
